<template>
  <div class="player-controls">
    <!-- AUDIO EFFECTS BUTTON (top right) -->
    <v-btn
      v-if="!isVideoMode && isLoggedIn && isMasterPlaylist"
      @click="openAudioEffectsDialog"
      fab
      x-small
      :outlined="hasActiveEffects"
      :class="['audio-effects-button', 'deactivateButtonBugFix', { 'fx-active': hasActiveEffects }]"
    >
      <span class="fx-text">FX</span>
    </v-btn>

    <!-- TRACK TITLE -->
    <v-row class="mx-2 mt-1 mt-md-3 mb-md-1 track-title-row">
      <div class="text--primary trackTitle prevent-select">
        {{ currentTrackName }}
      </div>
    </v-row>

    <!-- SEEK -->
    <v-row
      class="mx-2 progressSlider"
      @mousedown="progressSliderTapped"
      @touchstart="progressSliderTapped"
    >
      <div class="slider-waveform-container">
        <waveform-canvas
          v-if="!isVideoMode && currentTrackDetails"
          :track-name="currentTrackDetails.name"
          :duration="currentTrackDetails.duration"
        />
        <v-slider
          v-model="trackProgress"
          @change="userClickedOnProgressSlider"
          @mousedown="progressSliderTapped"
          @touchmove="progressSliderTapped"
          @mouseup="progressSliderReleased"
          @touchend="progressSliderReleased"
          @drag="progressSliderTapped"
          @drag-end="progressSliderReleased"
          color="primary"
          track-color="tertiary"
          hide-details
          :step="0"
          min="0"
          max="100"
        />
      </div>
    </v-row>

    <!-- TRACK TIMES -->
    <v-row
      justify="space-between"
      class="trackTimes mx-3 caption text--secondary prevent-select"
    >
      <div>{{ currentPosition }}</div>
      <div>{{ duration }}</div>
    </v-row>

    <!-- TRACK CONTROL BUTTONS -->
    <v-row
      justify="center"
      align="center"
      class="trackControlButtons prevent-select"
    >
      <!-- SHUFFLE -->
      <v-btn
        @click="toggleShuffle"
        class="mx-2"
        icon
        color="secondary"
        :outlined="isShuffleMode"
      >
        <v-icon>mdi-shuffle</v-icon>
      </v-btn>
      
      <!-- PREVIOUS -->
      <v-btn
        @click="previousTrack"
        class="mx-2"
        icon
        x-large
        color="secondary"
      >
        <v-icon>mdi-skip-previous</v-icon>
      </v-btn>
      
      <!-- PLAY/PAUSE -->
      <v-btn
        @click="togglePlayPause"
        class="mx-2"
        icon
        x-large
        color="secondary"
      >
        <transition name="spin-fade" mode="out-in">
          <v-progress-circular
            v-if="showLoadingSpinner"
            key="spinner"
            indeterminate
            color="primary"
            :size="28"
            :width="3"
          />
          <v-icon v-else key="icon">{{ isPlaying ? "mdi-pause" : "mdi-play" }}</v-icon>
        </transition>
      </v-btn>
      
      <!-- NEXT -->
      <v-btn
        @click="nextTrack"
        class="mx-2"
        icon
        x-large
        color="secondary"
      >
        <v-icon>mdi-skip-next</v-icon>
      </v-btn>
      
      <!-- REPEAT -->
      <v-btn
        @click="toggleRepeat"
        class="mx-2"
        icon
        color="secondary"
        :outlined="repeatMode > 0"
      >
        <v-icon>{{ repeatIcon }}</v-icon>
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AudioService from '@/services/AudioService';
import VideoService from '@/services/VideoService';
import WaveformCanvas from '@/components/player/WaveformCanvas.vue';

export default {
  name: 'PlayerControls',
  components: { WaveformCanvas },
  
  data() {
    return {
      trackProgress: 0,
      currentPosition: '00:00',
      duration: '00:00',
      progressAnimationFrame: null,
      shouldPreventTooManyClicks: false,
      isUserInteractingWithSlider: false,
      lastProgressUpdate: 0, // Timestamp of last progress update for throttling
      showLoadingSpinner: false,
      loadingSpinnerTimeout: null,
    };
  },

  watch: {
    isTrackLoading(loading) {
      if (loading) {
        this.loadingSpinnerTimeout = setTimeout(() => {
          this.showLoadingSpinner = true;
        }, 500);
      } else {
        clearTimeout(this.loadingSpinnerTimeout);
        this.showLoadingSpinner = false;
        this.updateTrackProgress();
      }
    },

    // The progress loop only runs while something is playing — no idle
    // 60 fps rAF loop when paused.
    isPlaying(playing) {
      if (playing && this._startProgressLoop) {
        this._startProgressLoop();
      } else if (!playing) {
        this.updateTrackProgress();
      }
    },
  },
  
  computed: {
    ...mapGetters({
      audioTrackDetails: 'player/currentTrackDetails',
      audioIsPlaying: 'player/isPlaying',
      audioIsShuffleMode: 'player/isShuffleMode',
      audioRepeatMode: 'player/repeatMode',
      audioRepeatIcon: 'player/repeatIcon',
      videoDetails: 'video/currentVideoDetails',
      videoIsPlaying: 'video/isPlaying',
      videoIsShuffleMode: 'video/isShuffleMode',
      videoRepeatMode: 'video/repeatMode',
      videoRepeatIcon: 'video/repeatIcon',
      isVideoMode: 'ui/isVideoMode',
      audioIsTrackLoading: 'player/isTrackLoading',
      hasActiveEffects: 'audioEffects/hasActiveEffects',
      isLoggedIn: 'auth/isLoggedIn',
      playlistURL: 'playlist/playlistURL',
    }),

    isMasterPlaylist() {
      return this.playlistURL === null;
    },

    currentTrackDetails() {
      return this.isVideoMode ? this.videoDetails : this.audioTrackDetails;
    },

    isPlaying() {
      return this.isVideoMode ? this.videoIsPlaying : this.audioIsPlaying;
    },

    isShuffleMode() {
      return this.isVideoMode ? this.videoIsShuffleMode : this.audioIsShuffleMode;
    },

    repeatMode() {
      return this.isVideoMode ? this.videoRepeatMode : this.audioRepeatMode;
    },

    repeatIcon() {
      return this.isVideoMode ? this.videoRepeatIcon : this.audioRepeatIcon;
    },

    currentTrackName() {
      return this.currentTrackDetails ? this.currentTrackDetails.name : ' ';
    },
    
    isTrackLoading() {
      return !this.isVideoMode && this.audioIsTrackLoading;
    },
  },
  
  mounted() {
    // Progress updates via rAF, but the loop only runs while playing (see
    // isPlaying watcher) and reads/DOM updates are throttled to ~10x/second
    // — smooth on a desktop-width slider without per-frame Vuetify work.
    const PROGRESS_UPDATE_INTERVAL_MS = 100;
    let lastUpdate = 0;

    const updateLoop = (timestamp) => {
      this.progressAnimationFrame = null;
      if (!this.isPlaying) {
        return; // loop parks; the isPlaying watcher restarts it
      }
      if (
        !this.isUserHoldingProgressSlider &&
        !this.isUserInteractingWithSlider &&
        timestamp - lastUpdate >= PROGRESS_UPDATE_INTERVAL_MS
      ) {
        lastUpdate = timestamp;
        this.updateTrackProgress();
      }
      this.progressAnimationFrame = requestAnimationFrame(updateLoop);
    };

    this._startProgressLoop = () => {
      if (this.progressAnimationFrame) return;
      this.progressAnimationFrame = requestAnimationFrame(updateLoop);
    };

    if (this.isPlaying) {
      this._startProgressLoop();
    }

    // Initialize progress indicators
    this.updateTrackProgress();
  },
  
  beforeDestroy() {
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
    }
    clearTimeout(this.loadingSpinnerTimeout);
  },
  
  methods: {
    ...mapActions({
      toggleAudioPlayPause: 'player/togglePlayPause',
      previousAudioTrack: 'player/previousTrack',
      nextAudioTrack: 'player/nextTrack',
      toggleAudioShuffle: 'player/toggleShuffle',
      toggleAudioRepeat: 'player/toggleRepeat',
      seekAudioToPosition: 'player/seekToPosition',
      startHoldingAudioSlider: 'player/startHoldingProgressSlider',
      stopHoldingAudioSlider: 'player/stopHoldingProgressSlider',
      toggleVideoPlayPause: 'video/togglePlayPause',
      previousVideo: 'video/previousVideo',
      nextVideo: 'video/nextVideo',
      toggleVideoShuffle: 'video/toggleShuffle',
      toggleVideoRepeat: 'video/toggleRepeat',
      seekVideoToPosition: 'video/seekToPosition',
      setVideoUserHoldingSlider: 'video/startHoldingProgressSlider',
    }),

    togglePlayPause() {
      if (this.shouldPreventTooManyClicks) return;

      if (this.isVideoMode) {
        this.toggleVideoPlayPause();
      } else {
        this.toggleAudioPlayPause();
      }

      // Prevent rapid clicking
      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    previousTrack() {
      if (this.shouldPreventTooManyClicks) return;

      if (this.isVideoMode) {
        this.previousVideo();
      } else {
        this.previousAudioTrack();
      }

      // Prevent rapid clicking
      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    nextTrack() {
      if (this.shouldPreventTooManyClicks) return;

      if (this.isVideoMode) {
        this.nextVideo();
      } else {
        this.nextAudioTrack();
      }

      // Prevent rapid clicking
      this.shouldPreventTooManyClicks = true;
      setTimeout(() => {
        this.shouldPreventTooManyClicks = false;
      }, 500);
    },

    toggleShuffle() {
      if (this.isVideoMode) {
        this.toggleVideoShuffle();
      } else {
        this.toggleAudioShuffle();
      }
    },

    toggleRepeat() {
      if (this.isVideoMode) {
        this.toggleVideoRepeat();
      } else {
        this.toggleAudioRepeat();
      }
    },

    openAudioEffectsDialog() {
      this.$store.dispatch('audioEffects/openDialog');
    },

    updateTrackProgress() {
      // Only update progress if current track details exist
      if (!this.currentTrackDetails) {
        return;
      }

      const progress = this.isVideoMode
        ? VideoService.getVideoProgress()
        : AudioService.getTrackProgress();

      if (progress) {
        const newProgress = progress.progressPercent || progress.percent || 0;
        const newPosition = progress.currentPosition || progress.currentTimeFormatted || '00:00';
        const newDuration = progress.duration || progress.durationFormatted || this.currentTrackDetails.duration || '00:00';

        // Only update slider if change is significant (reduces unnecessary reactivity)
        // 0.05% is well under a pixel on a desktop-width slider
        if (Math.abs(this.trackProgress - newProgress) > 0.05) {
          this.trackProgress = newProgress;
        }

        // Only update time displays if they've actually changed
        if (this.currentPosition !== newPosition) {
          this.currentPosition = newPosition;
        }
        if (this.duration !== newDuration) {
          this.duration = newDuration;
        }
      }
    },

    progressSliderTapped() {
      // Set local flag immediately to stop updates
      this.isUserInteractingWithSlider = true;

      if (this.isVideoMode) {
        this.setVideoUserHoldingSlider();
      } else {
        this.startHoldingAudioSlider();
      }
    },

    progressSliderReleased() {
      // Clear local flag to resume updates
      this.isUserInteractingWithSlider = false;

      if (this.isVideoMode) {
        this.$store.dispatch('video/stopHoldingProgressSlider');
      } else {
        this.stopHoldingAudioSlider();
      }
    },

    userClickedOnProgressSlider(newValue) {
      if (this.isVideoMode) {
        this.seekVideoToPosition(newValue);
      } else {
        this.seekAudioToPosition(newValue);
      }
      // While paused the progress loop is parked — sync the display once
      // after the seek settles
      setTimeout(() => this.updateTrackProgress(), 150);
    },
  }
};
</script>

<style scoped>
.player-controls {
  position: relative;
}

.audio-effects-button {
  position: absolute;
  top: -8px;
  right: 8px;
  z-index: 10;
}

/* Glowing animation when effects are active */
.fx-active {
  animation: fx-glow 3s ease-in-out infinite;
}

@keyframes fx-glow {
  0%, 100% {
    background-color: transparent;
  }
  50% {
    background-color: rgba(160, 160, 160, 0.3);
  }
}

/* Alignment for screens 600px+ */
/* Align track title, times, and FX button with slider thumb edges */
@media only screen and (min-width: 600px) {
  .track-title-row {
    margin-left: 10px !important;
    margin-right: 10px !important;
  }

  .trackTimes {
    margin-left: 10px !important;
    margin-right: 10px !important;
  }

  .audio-effects-button {
    right: 10px;
  }
}

/* md breakpoint and above (960px+) */
@media only screen and (min-width: 960px) {
  .audio-effects-button {
    top: -4px;
  }
}

/* Below 600px - move button to top right corner */
@media only screen and (max-width: 599px) {
  .audio-effects-button {
    top: -12px;
    right: 4px;
  }
}

.fx-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.trackTitle {
  white-space: nowrap;
  word-break: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 48px; /* Space for the button */
}

.trackTimes {
  margin-top: 9px;
}

@media only screen and (max-width: 550px) {
  .trackTimes {
    margin-top: 9px !important;
  }

  .trackControlButtons {
    margin-top: 7px !important;
  }
}

@media only screen and (max-width: 450px) {
  .progressSlider {
    margin-top: 4px !important;
  }
}

/* Fix Vuetify slider focus ripple offset on touch devices (iOS Safari) */
@media (pointer: coarse) {
  .progressSlider >>> .v-slider__thumb::before {
    display: none;
  }
}

.slider-waveform-container {
  position: relative;
  width: 100%;
}

/* Prevent scroll interference during slider interaction on iOS Safari */
.progressSlider {
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Play button loading spinner crossfade */
.spin-fade-enter-active, .spin-fade-leave-active {
  transition: opacity 0.2s ease;
}
.spin-fade-enter, .spin-fade-leave-to {
  opacity: 0;
}
</style>